import { FormSchema, OptionShape } from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import React, { MouseEventHandler, useContext, useEffect, useState } from 'react';
import ProjectContext from '../../../contexts/ProjectContext';
import ProjectPrintContext, {
  InspectionDateFilter,
  PrintProjectFormData,
} from '../../../contexts/ProjectPrintContext';
import { getMapOptionsSchema } from '../../Inspections/Details/SiteMapPrint';
import LoadingModal from '../../Shared/Loading/LoadingModal';
import PrintModal from '../../Shared/Print/PrintModal';
import { useProjectSiteMapInspections } from '../../../hooks/inspection';
import projectInspectionTemplatesApi from '../../../utils/api/projectInspectionTemplates';

export type SiteMapSchemaOptions = {
  inspections: OptionShape[];
  filter: InspectionDateFilter;
  filterOptions: OptionShape[];
  siteMapTextLabel: string;
  targetDocId?: number;
  targetDoc?: any;
  projectLevelEsriEnabled?: boolean;
};

export const getSiteMapFilterSchema = ({
  inspections,
  filter,
  filterOptions,
  siteMapTextLabel,
}: SiteMapSchemaOptions): FormSchema => ({
  inspection_label: {
    label: 'Site Maps',
    controlType: UIControlType.plainText,
    style: { marginBottom: 0 },
    className: inspections.length === 0 ? 'hidden' : '',
  },
  timeFilter: {
    controlType: UIControlType.select,
    parse: Number,
    label: 'Filter',
    options: filterOptions,
    style: { flex: '0 1 33%', paddingLeft: '2rem' },
  },
  start_date: {
    controlType: UIControlType.date,
    disabled: (filter.prevMonths || 0) > 0,
    label: 'Start Date',
    style: { flex: '0 1 33%' },
    popperPlacement: 'top',
  },
  end_date: {
    controlType: UIControlType.date,
    disabled: (filter.prevMonths || 0) > 0,
    label: 'End Date',
    style: { flex: '0 1 33%' },
    popperPlacement: 'top',
    validation: {
      maxDate: new Date(),
    },
  },
  siteMapText: {
    controlType: UIControlType.plainText,
    label: siteMapTextLabel,
    style: { paddingLeft: '2rem', marginBottom: '1rem' },
  },
  currentVersion: {
    controlType: UIControlType.hidden,
  },
  inspectionIds: {
    controlType: UIControlType.radio,
    options: inspections.map((inspection) => ({
      ...inspection,
      label: inspection.label,
      value: inspection.value.toString() + '|' + inspection.label,
    })),
    validation: { required: true },
    style: { paddingLeft: '2rem' },
    className:
      inspections.length === 0
        ? 'hidden'
        : 'form-group--checkboxGroup two-columns',
  },
});

/**
 * Client-side filter: given all site map inspections and a selected template type id,
 * return only inspections whose template matches that type.
 * The "Current Version" entry (first item) is always included when there's a match.
 */
function filterSiteMapInspectionsByFormType(
  allInspections: any[],
  selectedFormType: string | number | undefined,
): any[] {
  if (!selectedFormType) {
    return [];
  }

  const typeId = Number(selectedFormType);

  // Filter inspections by template type, but always keep "Current Version" entries
  const filtered = allInspections.filter((insp: any) => {
    // "Current Version" entries have type === "Current Version" and no template
    if (insp.type === 'Current Version') {
      return true;
    }

    return insp.inspection_template?.inspection_type_id === typeId;
  });

  // If only "Current Version" entries remain (no actual inspections matched), 
  // still return them as they represent the current state
  return filtered;
}

const getPrintSiteMapsSchema = (
  options: SiteMapSchemaOptions & {
    formTypeOptions: { label: string; value: number | string }[];
    selectedFormType: string | number | undefined;
    isGISEnabled?: boolean;
  },
) => {
  const schema: FormSchema = {
    ...getMapOptionsSchema(options.targetDocId, options.targetDoc, options.projectLevelEsriEnabled, options.isGISEnabled),
    include_archive_map: {
      label: 'Include Archived Maps',
      controlType: UIControlType.checkbox,
      style: {},
    },
  };

  // Always show form type selector
  if (options.formTypeOptions.length > 0) {
    schema.siteMapFormType = {
      controlType: UIControlType.select,
      label: 'Form Type*',
      options: options.formTypeOptions,
      validation: { required: true },
      placeholder: 'Select a form type',
    };
  }

  // Only show site map selection when a form type is selected
  if (options.selectedFormType) {
    Object.assign(schema, getSiteMapFilterSchema(options));
  }

  return schema;
};

export interface IPrintSiteMapsModal {
  onClose: MouseEventHandler<Element>;
  targetDoc?: any;
  isGISEnabled?: boolean;
}

const PrintSiteMapsModal: React.FC<IPrintSiteMapsModal> = ({
  onClose,
  targetDoc,
  isGISEnabled
}) => {
  const projectContext = useContext(ProjectContext);
  const projectPrintContext = useContext(ProjectPrintContext);
  const [formTypeOptions, setFormTypeOptions] = useState<{ label: string; value: number | string }[]>([]);

  const targetDocId = targetDoc ? targetDoc.id : 0;

  const loadFormTypes = async () => {
    const projectId = projectContext.project?.id;

    if (!projectId) return;

    try {
      const templateTypes = await projectInspectionTemplatesApi.getUniqueTemplateTypes(projectId);

      if (templateTypes && templateTypes.length > 0) {
        const options = templateTypes.map((t) => ({
          label: t.name,
          value: t.id,
        }));

        setFormTypeOptions(options);
      } else {
        setFormTypeOptions([]);
      }
    } catch {
      setFormTypeOptions([]);
    }
  };

  useEffect(() => {
    loadFormTypes();
  }, []);

  if (!projectContext.project || projectContext.inspectionsQuery.isFetching) {
    return <LoadingModal show />;
  }

  const inspectionsSiteMapQuery = useProjectSiteMapInspections(
    projectContext.project.id,
    targetDocId,
  );

  const allInspectionSiteMapQuery = useProjectSiteMapInspections(
    projectContext.project.id,
    0,
  );

  if (inspectionsSiteMapQuery.isFetching) {
    return <LoadingModal show />;
  }

  const validateInspectionData = (formData: PrintProjectFormData) => {
    let validatedFormData = JSON.parse(JSON.stringify(formData));

    if (validatedFormData.inspectionIds.split('|')[2].trim() === 'Current Version') {
      validatedFormData.currentVersion=true;
    }

    validatedFormData.inspectionIds = [validatedFormData.inspectionIds.split('|')[0]];

    return validatedFormData;
  };

  return (
    <PrintModal
      jobName={'sitemaps'}
      className='print-inspections-modal'
      jobData={{
        projectId: projectContext.project.id,
        targetDocId: targetDoc ? targetDoc.id : null,
      }}
      statusUpdateKey={projectContext.project.id}
      onClose={onClose}
      formSchema={(formContextValue: PrintProjectFormData) => {
        const filter: InspectionDateFilter = {
          start: formContextValue.start_date,
          end: formContextValue.end_date,
          prevMonths: formContextValue.timeFilter,
        };

        const selectedFormType = formContextValue.siteMapFormType;

        // Get all site map inspections
        const allSiteMapInspections = inspectionsSiteMapQuery.data || [];

        // Filter by selected form type
        const typeFilteredInspections = filterSiteMapInspectionsByFormType(
          allSiteMapInspections,
          selectedFormType,
        );

        const inspections = projectPrintContext.getInspectionOptions(
          typeFilteredInspections,
          filter,
        );

        // Fallback: if no inspections match for the specific site map, try all site maps
        const fallbackInspections = inspections.length > 0
          ? inspections
          : selectedFormType
            ? projectPrintContext.getInspectionOptions(
                filterSiteMapInspectionsByFormType(
                  allInspectionSiteMapQuery.data?.slice(0, 1) || [],
                  selectedFormType,
                ),
                filter,
              )
            : [];

        return getPrintSiteMapsSchema({
          inspections: fallbackInspections,
          filter,
          filterOptions: projectPrintContext.timeFilterOptions,
          siteMapTextLabel: selectedFormType
            ? projectPrintContext.getSiteMapText({
                inspections: fallbackInspections,
                filter,
              })
            : 'Select a form type to view site maps',
          targetDocId,
          formTypeOptions,
          selectedFormType,
          targetDoc,
          projectLevelEsriEnabled: projectContext.project?.esri_layers_enabled,
          isGISEnabled,
        });
      }}
      initialFormData={{
        inspectionIds: {},
        include_findings: false,
        projectId: projectContext.project.id,
        timeFilter: 3,
        currentVersion: false,
        include_map_changelog: false,
        include_legend_item_tags: false,
        include_blank_maps: false,
        include_esri_layers: false,
      }}
      modalTitle='Print Site Maps'
      validateData={validateInspectionData}
    />
  );
};

export default PrintSiteMapsModal;
